// ManualCard.tsx - Navy/Gold Theme with FREE module support
import { Link } from 'react-router-dom'
import { Clock, Star, BookOpen, Gift } from 'lucide-react'
import type { Manual } from '../../config/series'
import SeriesIcon from '../ui/SeriesIcon'

interface ManualCardProps {
  manual: Manual & { free?: boolean }
  seriesSlug: string
  seriesIcon?: string
  seriesColor?: string
}

export default function ManualCard({ manual, seriesSlug: _seriesSlug, seriesIcon }: ManualCardProps) {
  const displayIcon = manual.icon || seriesIcon

  const difficultyConfig: Record<string, { bg: string; text: string; label: string }> = {
    beginner: { bg: 'bg-green-500', text: 'text-white', label: 'Beginner' },
    intermediate: { bg: 'bg-gold-600', text: 'text-navy-800', label: 'Intermediate' },
    advanced: { bg: 'bg-red-500', text: 'text-white', label: 'Advanced' },
  }

  const difficulty = typeof manual.difficulty === 'string' ? manual.difficulty.toLowerCase() : 'beginner'
  const difficultyStyle = difficultyConfig[difficulty] || difficultyConfig['beginner']

  const isFree = manual.free === true || manual.price === 0

  return (
    <div className="bg-white dark:bg-navy-800 rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 border border-gray-200 dark:border-navy-700 overflow-hidden group hover:-translate-y-1">
      <div
        className={`p-4 ${isFree ? 'bg-gradient-to-r from-green-600 to-emerald-700' : 'bg-gradient-to-r from-navy-800 to-navy-900'}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {displayIcon && <SeriesIcon icon={displayIcon} size="md" />}
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${difficultyStyle.bg} ${difficultyStyle.text}`}
            >
              {difficultyStyle.label}
            </span>
            {isFree && (
              <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-white text-green-700 flex items-center">
                <Gift className="w-3 h-3 mr-1" />
                FREE
              </span>
            )}
          </div>
          <div className="text-right">
            {isFree ? (
              <div className="text-2xl font-bold text-white">FREE</div>
            ) : (
              <div className="text-xs font-bold text-gold-400 uppercase tracking-wide text-right max-w-[140px]">
                Included in library
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-6">
        <div
          className="font-serif text-xl font-bold mb-3 transition-colors line-clamp-2 text-gray-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400"
          role="heading"
          aria-level={3}
        >
          {manual.title}
        </div>

        <p className="text-gray-700 dark:text-gray-300 mb-4 line-clamp-3 leading-relaxed text-sm">{manual.summary}</p>

        {isFree && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-700">
            <div className="text-sm text-green-900 dark:text-green-200 font-semibold">Free sample minimanual</div>
            <div className="text-xs text-green-800 dark:text-green-300 mt-1">
              Try our writing style and depth — no payment required.
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
            <Clock className="w-4 h-4 mr-1" />
            <span>{manual.duration}</span>
          </div>
          {manual.featured && (
            <div className="flex items-center text-sm text-amber-600 dark:text-amber-400 font-medium">
              <Star className="w-4 h-4 mr-1 fill-current" />
              <span>Featured</span>
            </div>
          )}
        </div>

        {manual.tags && manual.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {manual.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs rounded-md"
              >
                {tag.replace('-', ' ')}
              </span>
            ))}
            {manual.tags.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-md">
                +{manual.tags.length - 3} more
              </span>
            )}
          </div>
        )}

        {manual.prerequisites && manual.prerequisites.length > 0 && (
          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border-l-4 border-amber-500">
            <div className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">Prerequisites:</div>
            <div className="text-xs text-amber-700 dark:text-amber-200">
              {manual.prerequisites.map((prereq) => prereq.replace('-', ' ')).join(', ')}
            </div>
          </div>
        )}

        <div className="flex flex-col space-y-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <Link
              to={`/${manual.slug}`}
              className="flex-1 bg-navy-800 hover:bg-navy-900 text-white text-center py-3 rounded-lg font-semibold transition-all flex items-center justify-center hover:-translate-y-0.5 hover:shadow-lg"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              {isFree ? 'Read now' : 'View minimanual'}
            </Link>
            {!isFree && (
              <Link
                to="/subscribe"
                className="flex-1 bg-gold-600 hover:bg-gold-500 text-navy-800 text-center py-3 rounded-lg font-semibold transition-all flex items-center justify-center hover:-translate-y-0.5 hover:shadow-gold"
              >
                Get library access
              </Link>
            )}
          </div>
        </div>

        {manual.category && (
          <div className="mt-4 text-center">
            <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs rounded-full capitalize font-medium">
              {manual.category.replace('-', ' ')}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
